import { Schema } from "effect";
import type { AuditEvent } from "./audit.ts";

// The curriculum's two identity dimensions plus its lifecycle status (the curriculum-tags ADR,
// amended for the identity/status split). `level` and `language` are the machine-readable IDENTITY a
// document carries alongside its H1 display title — captured at Import (on the genesis event),
// inherited on Clone, and editable afterwards only from the curriculum page. `status` is the
// separate LIFECYCLE axis (was "stage"): the one dimension expected to change over a document's life,
// with archived folded in. Boundary schemas here so the REST edge, the audit events, and the MCP
// `list-curricula` rows all decode one definition.
//
// All three are CLOSED sets surfaced as dropdowns in the UI — the option lists below are the single
// source both the `Schema.Literal` (which rejects anything off-list at the decode boundary) and the
// UI `<select>` read, so a free-text level ("beginner") or an unknown language can never be stored.

// The CEFR levels and sublevels the studio recognises, coarse band first then its sublevels. The
// value the Level altitude groups UnitPlans by, here naming the level the whole curriculum targets;
// sibling lookup ("the previous level") is an exact string join on this value.
export const CEFR_LEVELS = [
  "A0",
  "A1",
  "A1.1",
  "A1.2",
  "A2",
  "A2.1",
  "A2.2",
  "B1",
  "B1.1",
  "B1.2",
  "B2",
  "B2.1",
  "B2.2",
  "C1",
  "C1.1",
  "C1.2",
  "C2",
] as const;
export const CefrLevel = Schema.Literal(...CEFR_LEVELS).pipe(Schema.brand("CefrLevel"));
export type CefrLevel = typeof CefrLevel.Type;

// The curriculum's language, as the L2 code the monorepo-wide registry (`language_codes.yml` in
// `LanguagePackStore`) maps to a display name. The dropdown shows the name; the stored value is the
// code. The compiled-in table of codes studio knows is `KNOWN_LANGUAGES` (language-pack-completeness).
//
// ADR-0111: a PATTERN brand, not the closed literal union it used to be. A closed union cannot
// admit a language that did not exist when studio was built, and admitting one is the entire point
// of uploading a pack — `language_codes.yml` in `LanguagePackStore` is the real registry, listed by
// `GET /api/language-packs/languages`.
//
// Widening is decode-safe for the audit log: every historical `document.created.v2` /
// `document.tags-updated` event holds one of the three original codes, all of which still match.
// The shape still rejects prose ("Mexican Spanish") at the boundary, which is what the closed union
// was protecting — sibling lookup joins on the code exactly, and a free-text language would break
// that join silently.
const LANGUAGE_CODE_SHAPE = /^[A-Z]{3}$/;
export const CurriculumLanguage = Schema.String.pipe(
  Schema.filter(
    (s) =>
      LANGUAGE_CODE_SHAPE.test(s) ||
      "CurriculumLanguage must be a Babbel language code: 2-5 uppercase letters (QMS, QAE, ITA, …)",
  ),
  Schema.brand("CurriculumLanguage"),
);
export type CurriculumLanguage = typeof CurriculumLanguage.Type;

// The document's release LIFECYCLE (was `stage`) — the one axis *expected* to change over its life,
// so it lives outside the identity tags. `exploratory` is a working copy (every Clone starts here),
// `latest` the newest reviewed line, `stable` the released one, and `archived` the terminal
// out-of-rotation state (the former archive lifecycle, folded in). A closed literal so an unknown
// status fails the decode; the order here is the promotion order the UI presents.
export const CURRICULUM_STATUSES = ["exploratory", "latest", "stable", "archived"] as const;
export const CurriculumStatus = Schema.Literal(...CURRICULUM_STATUSES);
export type CurriculumStatus = typeof CurriculumStatus.Type;

// ─── Role and phase: the two axes `CurriculumStatus` above was straining to carry ───
//
// The retired status axis welded two unrelated questions together — *which* document this is among
// its siblings, and *how ready* its content is. They are now separate, because they change for
// different reasons and at different rates. `CurriculumStatus` remains exported only because the
// retired `document.status-updated` / `document.archived` events reference it and the audit log is
// append-only; nothing projects it any more (see `curriculum-lifecycle.ts` for the fold that reads
// those legacy events and maps them onto the two axes below).

// ROLE — which document this is for its (language pair, level). Exactly one `canonical` is intended
// per level; the index surfaces a missing or duplicated canonical rather than the schema forbidding
// it, because the corpus predates the rule. `archived` moves here from the retired status axis: being
// out of rotation is a statement about which document to use, not about content readiness.
export const CURRICULUM_ROLES = ["canonical", "experiment", "archived"] as const;
export const CurriculumRole = Schema.Literal(...CURRICULUM_ROLES);
export type CurriculumRole = typeof CurriculumRole.Type;

// PHASE — how far through production the content is. `structure` is the generated outline under
// structural review, `exercises` is authored exercises under review, `published` is fit to release.
// FORWARD-ONLY: a document never regresses (a live document that needs earlier work opens a *rework*
// instead — see `curriculum-lifecycle.ts`). **The declaration order IS the rank**, so `phaseRank`
// derives from this array and the two cannot drift apart.
export const CURRICULUM_PHASES = ["structure", "exercises", "published"] as const;
export const CurriculumPhase = Schema.Literal(...CURRICULUM_PHASES);
export type CurriculumPhase = typeof CurriculumPhase.Type;

export const phaseRank = (phase: CurriculumPhase): number => CURRICULUM_PHASES.indexOf(phase);

// When each phase was entered, keyed by phase. Partial by construction: a document reports only the
// phases it actually reached, so a reader can never mistake "never got there" for "got there at the
// epoch". Derived entirely from event timestamps — nothing new is stored.
export const PhaseTimestamps = Schema.partial(
  Schema.Struct(
    Object.fromEntries(CURRICULUM_PHASES.map((phase) => [phase, Schema.DateTimeUtc])) as {
      readonly [K in CurriculumPhase]: typeof Schema.DateTimeUtc;
    },
  ),
);
export type PhaseTimestamps = typeof PhaseTimestamps.Type;

// Strictly forward. Same-phase is deliberately NOT an advance, so a repeated request is a no-op
// rather than a duplicate append.
export const isPhaseAdvance = (from: CurriculumPhase, to: CurriculumPhase): boolean =>
  phaseRank(to) > phaseRank(from);

// Opaque, studio-minted identifiers threaded into codexflow's `curriculum_id` / `level_id` job
// parameters (ADR-0099), so a launch attaches to an EXISTING curriculum/level rather than minting a
// fresh one per run. `curriculumId` is scoped per-LANGUAGE — one value shared across every CEFR-level
// document of that language, since codexflow's `curriculum_id` spans a language's documents, not one
// document (a terminology collision with the studio's own "Curriculum" noun, where one Curriculum is
// one document — ADR-0099's Decision section names this explicitly). `levelId` is scoped per
// (language, level) — one value shared by every document at that pair, never forkable per-document,
// because `enrichment_pipeline`'s `push_curriculum` task writes to
// `PUT /v2/curricula/{id}/levels/{levelId}`: two documents at the same (language, level) holding
// different `levelId`s would produce two CSS levels for one semantic level, not a harmless duplicate.
// Both invariants are named here but enforced at the READ boundary (`submitPush`,
// `enrichment-pipeline-status-client.ts`), not at write time or at the `Schema` level: `Schema` has no
// cross-document constraint mechanism, and `setCurriculumIdentity` — the write-time enforcer ADR-0099
// originally specified — was never built and still has no caller. `curriculumId`/`levelId` are set
// through the same unvalidated `setCurriculumTags` identity-edit action as `level`/`language`/`l1`
// (ADR-0117); a wrong value only does damage once it reaches CSS, so that is where it is checked.
//
// ADR-0113 briefly minted a fresh `curriculum_id`/`level_id` per publish instead of reading these off
// `CurriculumTags`, reasoning that nothing downstream depended on repeated pushes sharing an id.
// ADR-0117 reopened that decision: `push_curriculum`'s CSS-registry resolution (CON-1536) fails closed
// for a pair it has no live `levelOrder` for, so `submitPush` now reads `curriculumId`/`levelId` here
// instead of re-deriving a target the registry cannot always supply. See ADR-0117.
export const CurriculumId = Schema.String.pipe(Schema.brand("CurriculumId"));
export type CurriculumId = typeof CurriculumId.Type;

export const LevelId = Schema.String.pipe(Schema.brand("LevelId"));
export type LevelId = typeof LevelId.Type;

// The identity tag set: level + language, both required wherever tags are supplied (Import, the
// identity-edit action). A document with no identity tags at all (seeded or pre-tags legacy) simply
// has no tags-bearing event and surfaces as untagged until the Expert tags it. Status is NOT part of
// this set — it is projected from its own lifecycle events (see `curriculum-tags.ts` module doc).
export const CurriculumTags = Schema.Struct({
  level: CefrLevel,
  language: CurriculumLanguage,
  // `l1` — the LEARNER's language, as against `language` above, which is the L2 being taught. The
  // index groups by the (l1, language) pair, and sibling lookup joins on both exactly.
  //
  // OPTIONAL, deliberately: this struct is embedded in `document.created.v2` and
  // `document.tags-updated` events already written to the log, so a required field would fail to
  // decode every one of them. The import form requires it going forward (a UI-level rule); the
  // schema tolerates its absence (a log-level fact). Same additive precedent as ADR-0070's
  // `clonedFromVersion`. A document with no `l1` is simply un-paired until an Expert sets it.
  l1: Schema.optional(CurriculumLanguage),
  // `curriculumId`/`levelId` — the launch identity (ADR-0099, amended ADR-0117). Optional for the
  // same reason as `l1`: this struct is embedded in already-written audit events, so a required field
  // would break decode of every historical `document.created.v2`/`document.tags-updated`. Set via the
  // Metadata tab's Identity editor (the plain `setCurriculumTags` action — `setCurriculumIdentity`
  // still has zero callers), or written by Studio itself once a publish's push lands: `levelId` onto
  // every document at the (l1, language, level), `curriculumId` onto every document of the pair
  // (ADR-0119). `submitPush` reads both, minting whatever no document holds yet.
  curriculumId: Schema.optional(CurriculumId),
  levelId: Schema.optional(LevelId),
  // The units live in CSS at `levelId` (ADR-0122), in CSS's form (`unit_a2_2_1`, ADR-0124). Written
  // onto every document at the level once a push lands, or by hand for units pushed outside studio.
  // `submitPush` refuses a unit listed here. Optional for the same reason as `l1`.
  pushedUnitIds: Schema.optional(Schema.Array(Schema.String)),
});
export type CurriculumTags = typeof CurriculumTags.Type;

// The L1 a run takes for a document. `l1` is absent only on documents tagged before the field existed
// (the import form has required it since), and every one of those was authored for American English,
// so absence reads as QAE — a legacy read, like the exercise schema's "American English" alias
// (ADR-0127). Every other L1 is the document's own tag; no run defaults an L1 it was not given.
export const LEGACY_L1 = "QAE";
export const curriculumL1 = (tags: Pick<CurriculumTags, "l1">): string => tags.l1 ?? LEGACY_L1;

// CSS holds unpadded unit ids (`unit_a2_2_1`), and so does ingest (ADR-0124). Ids typed by hand may
// be padded, so unit ids are compared in this form.
export const unpaddedUnitId = (unitId: string): string => unitId.replace(/_0+(?=\d)/g, "_");

// The pure tags fold (R19): one document's ordered audit events in, its current identity tags out —
// the projection over the LATEST tags-bearing event on the chain (a tags-carrying genesis
// `document.created.v2`, or a later `document.tags-updated`). Mirrors the Postgres adapter's
// `DISTINCT ON (document_id) … WHERE tag IN (...) ORDER BY seq DESC` exactly, so a per-document read
// and a corpus-wide scan agree. `undefined` covers both "no tags-bearing event at all" and "the
// winning event carried no tags" (a tagless genesis never since updated).
export const foldCurriculumTags = (events: readonly AuditEvent[]): CurriculumTags | undefined => {
  let tags: CurriculumTags | undefined;
  for (const event of events) {
    if (event._tag === "document.created.v2" || event._tag === "document.tags-updated") {
      tags = event.tags;
    }
  }
  return tags;
};
